import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase-client';
import { useAuth } from '../../auth/AuthContext';
import { useOnlineRefreshSignal } from '../../utils/onlineStatus';
import { readOfflineContentCache, writeOfflineContentCache } from '../../utils/offlineContentCache';
const HOME_TEAMS_FIELD = 'homeDashboardTeamIds';
const CACHE_SCOPE = 'teams';
function normalizeTeamIds(value) {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.filter(id => typeof id === 'string' && id.trim() !== '').map(id => id.trim())));
}
function getHomeTeamCacheParts(userId) {
  return [userId, CACHE_SCOPE, 'homeDashboardTeamIds'];
}
export function useHomeScreenTeams() {
  const {
    user
  } = useAuth();
  const [homeTeamIds, setHomeTeamIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTeamId, setSavingTeamId] = useState('');
  const onlineRefreshSignal = useOnlineRefreshSignal();
  const reload = useCallback(async () => {
    if (!user?.uid) {
      setHomeTeamIds([]);
      setLoading(false);
      return;
    }
    const cachedIds = normalizeTeamIds(readOfflineContentCache(getHomeTeamCacheParts(user.uid), []));
    if (cachedIds.length > 0) {
      setHomeTeamIds(cachedIds);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      if (!userSnap.exists()) {
        if (!userSnap.metadata?.fromCache) {
          setHomeTeamIds([]);
          writeOfflineContentCache(getHomeTeamCacheParts(user.uid), []);
        }
        return;
      }
      const nextIds = normalizeTeamIds(userSnap.data()?.[HOME_TEAMS_FIELD]);
      if (nextIds.length === 0 && userSnap.metadata?.fromCache && cachedIds.length > 0) return;
      setHomeTeamIds(nextIds);
      if (nextIds.length > 0 || !userSnap.metadata?.fromCache) {
        writeOfflineContentCache(getHomeTeamCacheParts(user.uid), nextIds);
      }
    } catch (err) {
      if (cachedIds.length === 0) setHomeTeamIds([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);
  useEffect(() => {
    reload();
  }, [onlineRefreshSignal, reload]);
  const setTeamVisibleOnHome = useCallback(async (teamId, shouldShow) => {
    if (!user?.uid || !teamId) {
      return {
        ok: false,
        shown: false,
        error: new Error('Missing user or team id.')
      };
    }
    const normalizedTeamId = String(teamId).trim();
    if (!normalizedTeamId) {
      return {
        ok: false,
        shown: false,
        error: new Error('Invalid team id.')
      };
    }
    setSavingTeamId(normalizedTeamId);
    const currentlyShown = homeTeamIds.includes(normalizedTeamId);
    const nextShown = typeof shouldShow === 'boolean' ? shouldShow : !currentlyShown;
    const nextIds = nextShown ? Array.from(new Set([...homeTeamIds, normalizedTeamId])) : homeTeamIds.filter(id => id !== normalizedTeamId);
    setHomeTeamIds(nextIds);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        [HOME_TEAMS_FIELD]: nextIds
      });
      writeOfflineContentCache(getHomeTeamCacheParts(user.uid), nextIds);
      return {
        ok: true,
        shown: nextShown
      };
    } catch (err) {
      // Restore previous state on failure.
      setHomeTeamIds(homeTeamIds);
      return {
        ok: false,
        shown: currentlyShown,
        error: err
      };
    } finally {
      setSavingTeamId('');
    }
  }, [homeTeamIds, user?.uid]);
  const setHomeTeamOrder = useCallback(async (orderedTeamIds = []) => {
    if (!user?.uid) {
      return {
        ok: false,
        error: new Error('Missing user id.')
      };
    }
    const nextIds = normalizeTeamIds(orderedTeamIds);
    const currentIds = normalizeTeamIds(homeTeamIds);
    setSavingTeamId('__home-order__');
    setHomeTeamIds(nextIds);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        [HOME_TEAMS_FIELD]: nextIds
      });
      writeOfflineContentCache(getHomeTeamCacheParts(user.uid), nextIds);
      return {
        ok: true,
        ids: nextIds
      };
    } catch (err) {
      setHomeTeamIds(currentIds);
      return {
        ok: false,
        error: err
      };
    } finally {
      setSavingTeamId('');
    }
  }, [homeTeamIds, user?.uid]);
  return {
    homeTeamIds,
    loading,
    savingTeamId,
    setTeamVisibleOnHome,
    setHomeTeamOrder,
    reload
  };
}
